import { toIsoDate } from "@/lib/dateInput";

export interface InstallmentDraft {
  number: number;
  amount: number;
  dueDate: string;
}

export function generateInstallments(
  totalValue: number,
  count: number,
  startDate: string
): InstallmentDraft[] {
  if (count < 1) return [];

  const perInstallment = Math.floor((totalValue * 100) / count) / 100;
  const installments: InstallmentDraft[] = [];
  const [year, month, day] = startDate.split("-").map(Number);

  for (let i = 0; i < count; i++) {
    const amount =
      i === count - 1
        ? Math.round((totalValue - perInstallment * (count - 1)) * 100) / 100
        : perInstallment;

    const due = new Date(year, month - 1 + i, day);
    const dueDate = toIsoDate(due);

    installments.push({
      number: i + 1,
      amount,
      dueDate,
    });
  }

  return installments;
}

export function sumInstallmentAmounts(installments: InstallmentDraft[]): number {
  return Math.round(installments.reduce((sum, i) => sum + i.amount, 0) * 100) / 100;
}

export function recalculateDownstreamAmounts(
  installments: InstallmentDraft[],
  editedIndex: number,
  totalValue: number
): InstallmentDraft[] {
  if (editedIndex < 0 || editedIndex >= installments.length) return installments;

  const lockedSum = installments
    .slice(0, editedIndex + 1)
    .reduce((sum, installment) => sum + installment.amount, 0);
  const downstreamCount = installments.length - editedIndex - 1;

  if (downstreamCount === 0) return installments;

  const remaining = totalValue - lockedSum;
  const perInstallment = Math.floor((remaining * 100) / downstreamCount) / 100;

  return installments.map((installment, index) => {
    if (index <= editedIndex) return installment;

    const downstreamIndex = index - editedIndex - 1;
    const isLast = downstreamIndex === downstreamCount - 1;
    const amount = isLast
      ? Math.round((remaining - perInstallment * (downstreamCount - 1)) * 100) / 100
      : perInstallment;

    return { ...installment, amount };
  });
}

export function recalculateDownstreamDueDates(
  installments: InstallmentDraft[],
  editedIndex: number
): InstallmentDraft[] {
  if (editedIndex < 0 || editedIndex >= installments.length) return installments;

  const anchor = installments[editedIndex];
  const [year, month, day] = anchor.dueDate.split("-").map(Number);

  return installments.map((installment, index) => {
    if (index <= editedIndex) return installment;

    const offset = index - editedIndex;
    const due = new Date(year, month - 1 + offset, day);
    return { ...installment, dueDate: toIsoDate(due) };
  });
}
